import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Form, Input, Space, Typography, Tag, Popconfirm, message, Empty, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import parameterTypeService, { ParameterType } from '../../services/parameterTypeService';
import { SplitPaneLayout } from '../common/SplitPaneLayout';
import { ManagementForm } from '../common/ManagementForm';
import '../../styles/management-pages.css';
import './ParameterTypeList.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ParameterTypeList: React.FC = () => {
  const [parameterTypes, setParameterTypes] = useState<ParameterType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();

  const selectedType = selectedTypeId 
    ? parameterTypes.find(t => t.id === selectedTypeId) 
    : null;

  useEffect(() => {
    void loadParameterTypes();
  }, []);

  const loadParameterTypes = async () => {
    setLoading(true);
    try {
      const types = await parameterTypeService.getAll();
      setParameterTypes(types);
    } catch (error) {
      console.error('Error loading parameter types:', error);
      message.error('Failed to load parameter types');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateType = () => {
    // Create a temporary type object for the list
    const tempId = `temp-${Date.now()}`;
    const newType: ParameterType = {
      id: tempId,
      name: '',
      description: null,
      created_at: new Date().toISOString(),
      updated_at: null,
    };
    
    // Add to beginning of list
    setParameterTypes([newType, ...parameterTypes]);
    setSelectedTypeId(tempId);
    setIsCreating(true);
    form.resetFields();
  };

  const handleSelectType = (type: ParameterType) => {
    setSelectedTypeId(type.id);
    setIsCreating(false);
    
    // Set form values
    form.setFieldsValue({
      name: type.name,
      description: type.description || '',
    });
  };

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
      if (isCreating && selectedTypeId?.startsWith('temp-')) {
        // Create new type
        const newType = await parameterTypeService.create({
          name: values.name.trim(),
          description: values.description?.trim() || '',
        });
        message.success('Parameter type created successfully');
        
        // Remove temp item and add real one
        setParameterTypes(parameterTypes.filter(t => t.id !== selectedTypeId).concat(newType));
        setSelectedTypeId(newType.id);
        setIsCreating(false);
      } else if (selectedType) {
        // Update existing type
        const updatedType = await parameterTypeService.update(selectedType.id, {
          name: values.name.trim(),
          description: values.description?.trim() || '',
        });
        message.success('Parameter type updated successfully');
        
        // Update in list
        setParameterTypes(parameterTypes.map(t => t.id === selectedType.id ? updatedType : t));
        setSelectedTypeId(updatedType.id);
      }
      
      await loadParameterTypes();
    } catch (error: any) {
      console.error('Error saving parameter type:', error);
      message.error(error.message || 'Failed to save parameter type');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreating && selectedTypeId?.startsWith('temp-')) {
      // Remove temp item from list
      setParameterTypes(parameterTypes.filter(t => t.id !== selectedTypeId));
    }
    setSelectedTypeId(null);
    setIsCreating(false);
    form.resetFields();
  };

  const handleDelete = async () => {
    if (!selectedType) return;
    
    try {
      await parameterTypeService.delete(selectedType.id);
      message.success('Parameter type deleted successfully');
      setSelectedTypeId(null);
      setIsCreating(false);
      await loadParameterTypes();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete parameter type');
      throw error;
    }
  };

  const handleDeleteFromTable = async (id: string) => {
    try {
      await parameterTypeService.delete(id);
      message.success('Parameter type deleted successfully');
      if (selectedTypeId === id) {
        setSelectedTypeId(null);
        setIsCreating(false);
      }
      await loadParameterTypes();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete parameter type');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, type: ParameterType) => (
        <Button
          type="link"
          onClick={() => handleSelectType(type)}
          style={{ padding: 0 }}
        >
          <Text strong>{text || '(Unnamed)'}</Text>
        </Button>
      ),
      sorter: (a: ParameterType, b: ParameterType) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || <Text type="secondary">No description</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, type: ParameterType) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleSelectType(type)}
            title="Edit parameter type"
          />
          <Popconfirm
            title="Are you sure you want to delete this parameter type?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteFromTable(type.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete parameter type"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="management-page-container">
      <div className="management-page-header">
        <Title level={2}>Parameter Types</Title>
        <Text type="secondary">This Configuration page is leftover. Types from Prime live on the global property catalog.</Text>
      </div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Use Global properties instead"
        description={
          <span>
            Prime property types are defined on{' '}
            <Link to="/admin/global-properties">Platform administration → Global properties</Link>
            . This table is not filled by Prime import.
          </span>
        }
      />

      <SplitPaneLayout
        leftContent={
          <Card
            className="list-card"
            title={
              <Space>
                <Title level={4} style={{ margin: 0 }}>Parameter Types</Title>
                <Tag>{parameterTypes.length}</Tag>
              </Space>
            }
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadParameterTypes} size="small">
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateType}
                >
                  New
                </Button>
              </Space>
            }
          >
            <Table
              columns={columns.filter(col => col.key !== 'actions')}
              dataSource={parameterTypes}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} types`,
                position: ['bottomCenter'],
              }}
              scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
              onRow={(record) => ({
                onClick: () => handleSelectType(record),
                style: {
                  cursor: 'pointer',
                  backgroundColor: selectedTypeId === record.id ? '#e6f7ff' : 'transparent',
                },
              })}
              rowClassName={(record) =>
                selectedTypeId === record.id ? 'selected-row' : ''
              }
            />
          </Card>
        }
        rightContent={
          selectedType || isCreating ? (
            <ManagementForm
              title={isCreating ? 'New Parameter Type' : `Edit Parameter Type: ${selectedType?.name || ''}`}
              isCreating={isCreating}
              isLoading={false}
              isSaving={isSaving}
              form={form}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={!isCreating ? handleDelete : undefined}
              showDelete={!isCreating}
            >
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter parameter type name' }]}
              >
                <Input placeholder="Enter parameter type name" />
              </Form.Item>
              <Form.Item
                name="description"
                label="Description"
              >
                <TextArea rows={3} placeholder="Enter parameter type description" />
              </Form.Item>
            </ManagementForm>
          ) : (
            <Card className="details-card">
              <Empty
                description="Select a parameter type or click 'New' to create one"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          )
        }
      />
    </div>
  );
};

export default ParameterTypeList;
